import { useId, useState } from 'react';
import {
  ANSWER_MAX_CHARS,
  Button,
  Dialog,
  ErrorText,
  QUESTION_MAX_CHARS,
  textareaClass,
  type PublishQuestionRequest,
  type QuestionView,
} from '@dataroom/shared';

/**
 * "Add to Buyer Questions" (ADR-0039 §"Buyer Questions"): before an answered
 * question is shared with every buyer who can see the CIM, the admin reviews —
 * and may rewrite — the question and answer wording, so nothing in it reveals
 * which buyer asked. Defaults: the published wording when editing, else the
 * original question and the released answer. The asker is never published.
 */
export function PublishQuestionDialog({
  question: q,
  pending,
  error,
  onPublish,
  onClose,
}: {
  question: QuestionView;
  pending: boolean;
  error: unknown;
  onPublish: (wording: PublishQuestionRequest) => void;
  onClose: () => void;
}) {
  const questionId = useId();
  const answerId = useId();
  const editing = Boolean(q.published);
  const [question, setQuestion] = useState(q.published?.question ?? q.body);
  const [answer, setAnswer] = useState(q.published?.answer.body ?? q.answer?.body ?? '');
  // An edit keeps the documents shared at first publish unless asked to refresh them.
  const [refreshLinks, setRefreshLinks] = useState(false);
  const ready = question.trim() !== '' && answer.trim() !== '';

  return (
    <Dialog
      title={editing ? 'Edit the Buyer Questions wording' : 'Add to Buyer Questions'}
      description="Every buyer who can see the CIM will see this question and answer. Edit the wording so it doesn’t identify the buyer who asked — their name is never shown."
      onClose={onClose}
      busy={pending}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || !ready}
            onClick={() =>
              onPublish({
                question: question.trim(),
                answer: answer.trim(),
                ...(editing && refreshLinks ? { refreshLinks: true } : {}),
              })
            }
          >
            {pending ? 'Saving…' : editing ? 'Save wording' : 'Share with buyers'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor={questionId} className="text-sm font-medium">
            Question, as buyers will see it
          </label>
          <textarea
            id={questionId}
            rows={3}
            className={textareaClass}
            value={question}
            maxLength={QUESTION_MAX_CHARS}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={pending}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={answerId} className="text-sm font-medium">
            Answer, as buyers will see it
          </label>
          <textarea
            id={answerId}
            rows={5}
            className={textareaClass}
            value={answer}
            maxLength={ANSWER_MAX_CHARS}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={pending}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          The answer’s linked documents are shared too — each buyer sees only the ones they can
          already open.
        </p>
        {editing ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={refreshLinks}
              onChange={(e) => setRefreshLinks(e.target.checked)}
              disabled={pending}
            />
            <span>
              Also update the shared documents to the answer’s current links
              <span className="block text-xs text-muted-foreground">
                Otherwise buyers keep seeing the documents shared when you first published.
              </span>
            </span>
          </label>
        ) : null}
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}
